import type { ServiceAlert } from '@traintopia/contracts'
import { compareRoutes, isSubwayRoute, routeBadgeLabel } from '../../ui/colors'
import { alertActiveOnDate, sortAlerts } from '../../ui/alerts'
import { SUBWAY_GROUPS, belongsTo, indexLine, routeKind, toDateInput, type Kind, type When } from './lines'

export function maxAlertDate(upcoming: ServiceAlert[]): string | undefined {
  let max = 0
  for (const alert of upcoming) {
    for (const period of alert.activePeriods) {
      max = Math.max(max, period.end ?? period.start ?? 0)
    }
    max = Math.max(max, alert.startsAt ?? 0)
  }
  return max ? toDateInput(max * 1000) : undefined
}

export function alertsInScope(
  live: ServiceAlert[],
  upcoming: ServiceAlert[],
  when: When,
  dateFilter: string | null,
  dateInputToMs: (value: string) => number,
): ServiceAlert[] {
  if (when === 'now') return live
  if (!dateFilter) return upcoming
  const dayStart = dateInputToMs(dateFilter)
  return upcoming.filter((alert) => alertActiveOnDate(alert, dayStart))
}

export function alertsInGroup(alerts: ServiceAlert[], groupLines?: readonly string[]): ServiceAlert[] {
  if (!groupLines) return alerts
  const wanted = new Set(groupLines)
  return alerts.filter((alert) => alert.routeIds.some((route) => wanted.has(indexLine(route))))
}

export function alertsOnLines(alerts: ServiceAlert[], lines: string[]): ServiceAlert[] {
  if (!lines.length) return alerts
  const wanted = new Set(lines.map(indexLine))
  return alerts.filter((alert) => alert.routeIds.some((route) => wanted.has(indexLine(route))))
}

export function alertKindCounts(alerts: ServiceAlert[]): Record<Kind, number> {
  return {
    subway: alerts.filter((alert) => belongsTo(alert, 'subway')).length,
    bus: alerts.filter((alert) => belongsTo(alert, 'bus')).length,
    ferry: alerts.filter((alert) => belongsTo(alert, 'ferry')).length,
    rail: alerts.filter((alert) => belongsTo(alert, 'rail')).length,
    njbus: alerts.filter((alert) => belongsTo(alert, 'njbus')).length,
  }
}

export function filterAlerts(alerts: ServiceAlert[], kind: Kind): ServiceAlert[] {
  return sortAlerts(alerts).filter((alert) => belongsTo(alert, kind))
}

export function alertCountsByLine(alerts: ServiceAlert[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const alert of alerts) {
    for (const route of new Set(alert.routeIds)) {
      if (route) counts.set(route, (counts.get(route) ?? 0) + 1)
    }
  }
  return counts
}

export function subwayAlertCounts(alerts: ServiceAlert[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const alert of alerts) {
    for (const route of new Set(alert.routeIds.filter(isSubwayRoute).map(indexLine))) {
      counts.set(route, (counts.get(route) ?? 0) + 1)
    }
  }
  return counts
}

export function subwayGroupTotals(alerts: ServiceAlert[]): Map<string, number> {
  const totals = new Map<string, number>()
  for (const group of SUBWAY_GROUPS) {
    totals.set(group.name, alertsInGroup(alerts, group.lines).length)
  }
  return totals
}

export interface AlertSection {
  label: string | null
  alerts: ServiceAlert[]
}

export function sectionBusFavorites(
  alerts: ServiceAlert[],
  kind: Kind,
  favoriteRoutes: Iterable<string>,
): AlertSection[] {
  const favored = new Set([...favoriteRoutes].map((route) => route.toUpperCase()))
  if (kind !== 'bus' || favored.size === 0) return [{ label: null, alerts }]
  const mine: ServiceAlert[] = []
  const rest: ServiceAlert[] = []
  for (const alert of alerts) {
    ;(alert.routeIds.some((route) => favored.has(route.toUpperCase())) ? mine : rest).push(alert)
  }
  if (!mine.length) return [{ label: null, alerts: rest }]
  return [
    { label: 'Your lines', alerts: mine },
    ...(rest.length ? [{ label: 'Other lines', alerts: rest }] : []),
  ]
}

export function suggestAlertRoutes(
  counts: Map<string, number>,
  selected: string[],
  kind: Kind,
  query: string,
): string[] {
  const normalized = query.trim().toUpperCase()
  const matches = [...counts.keys()].filter((route) => {
    if (selected.includes(route)) return false
    if (routeKind(route) !== kind) return false
    // The printed number too: an NJ Transit route is filed as `NJB-12` but signed `113`.
    return !normalized || route.toUpperCase().includes(normalized) ||
      routeBadgeLabel(route).toUpperCase().includes(normalized)
  })
  return matches.sort((a, b) => {
    if (normalized) {
      const byPrefix = Number(!a.toUpperCase().startsWith(normalized))
        - Number(!b.toUpperCase().startsWith(normalized))
      if (byPrefix) return byPrefix
    }
    return compareRoutes(a, b)
  })
}
